import { useEffect, useState } from 'react';
import { generateFacilityReport } from '../../../../helpers/api';
import { getYears } from '../../../../helpers/dateHelpers';
import { reportTypes } from '../../../../helpers/appstate';
import {
  Autocomplete,
  Box,
  Card,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CalendarMonth, Science } from '@mui/icons-material';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

export default function PersonnelFacilityQuarter({ facilityList, facilityLoading, initFacility }) {
  const years = getYears();

  const [quarterList, setQuarterList] = useState([]);
  const [quarterLoading, setQuarterLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(initFacility);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (facilitySelected?.facilityId && selectedYear) {
      fetchQuarters();
    }
  }, [facilitySelected, selectedYear]);

  const fetchQuarters = async () => {
    setQuarterLoading(true);
    try {
      const res = await generateFacilityReport(facilitySelected?.facilityId, selectedYear);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setQuarterList(data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setQuarterLoading(false);
    }
  };

  // --- UI PARTIALS ---

  const renderCardHeader = () => (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'center' }}
      gap={2}
      mb={3}
    >
      <Box>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          {/* {selectedYear} */}
          Monitoring Schedule
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={0.5}>
          Monitoring dates for Sampling Points reading
        </Typography>
      </Box>

      {/* Controls Container */}
      <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
        <Autocomplete
          disablePortal
          options={facilityList || []}
          value={facilitySelected}
          getOptionLabel={(option) => option?.facilityName || ''}
          onChange={(e, value) => {
            if (value) setFacilitySelected(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select Facility"
              InputProps={{
                ...params.InputProps,
                sx: { borderRadius: '10px', bgcolor: '#f9fafb' }, // Modern soft input
              }}
            />
          )}
          size="small"
          sx={{ width: { xs: '100%', sm: '200px' } }}
        />

        <Select
          value={selectedYear}
          size="small"
          onChange={(e) => setSelectedYear(e.target.value)}
          sx={{ borderRadius: '10px', bgcolor: '#f9fafb', minWidth: '100px' }}
        >
          {years?.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );

  const renderContent = () => {
    // Shared modern Card styles (matching the stat cards)
    const cardStyles = {
      p: 3,
      borderRadius: '16px',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
      bgcolor: '#ffffff',
      height: '100%',
    };

    if (quarterLoading || facilityLoading) {
      return (
        <Card elevation={0} sx={cardStyles}>
          {renderCardHeader()}
          <Box mt={4}>
            <LoaderSkeleton />
            <LoaderSkeleton />
          </Box>
        </Card>
      );
    }

    if (!quarterList || quarterList.length < 1) {
      // Optional: Add a nice empty state here if you want
      return null;
    }

    return (
      <Card elevation={0} sx={cardStyles}>
        {renderCardHeader()}

        <Stack spacing={2}>
          {quarterList?.map((i, index) => {
            const isQAQC = i?.reportType === reportTypes.QAQC;
            const hasValidDate = i?.scheduleDate && !i.scheduleDate.startsWith('0001-01-01');

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: isQAQC ? alpha('#ff9800', 0.3) : 'divider',
                  bgcolor: isQAQC ? alpha('#ff9800', 0.03) : 'transparent',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: isQAQC ? alpha('#ff9800', 0.08) : alpha('#000000', 0.02),
                  },
                }}
              >
                {/* Left Side: Badge & Title */}
                <Box display="flex" alignItems="center" gap={2}>
                  {/* Modern Quarter Badge */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      bgcolor: isQAQC ? alpha('#ff9800', 0.15) : alpha('#1976d2', 0.1),
                      color: isQAQC ? '#ed6c02' : '#1976d2',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isQAQC && <Science fontSize="small" />}Q{i?.reportQuarter}
                  </Box>

                  {/* Title & Warning */}
                  <Box>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {i?.reportTitle || `Quarter ${i?.reportQuarter} Report`}
                    </Typography>
                    {isQAQC && (
                      <Typography variant="caption" color="warning.main" fontWeight={500}>
                        Reserved for QAQC
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Right Side: Date Display */}
                {hasValidDate && (
                  <Chip
                    icon={<CalendarMonth fontSize="small" />}
                    label={`Monitoring: ${new Date(i?.scheduleDate).toLocaleDateString('en-NG', {
                      dateStyle: 'medium',
                    })}`}
                    variant="outlined"
                    sx={{
                      borderRadius: '8px',
                      borderColor: 'divider',
                      bgcolor: '#ffffff',
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      </Card>
    );
  };

  return <Box>{renderContent()}</Box>;
}
